import React, { useState, useEffect } from 'react';
import LinkArrow from '../../global/LinkArrow';

const Carousel = ({ quotes, carousel = false }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [intervalId, setIntervalId] = useState<NodeJS.Timeout | null>(null);

  const startInterval = () => {
    if (carousel && quotes.length > 1) {
      // Clear any existing interval first
      if (intervalId) clearInterval(intervalId);

      // Start new interval
      const newInterval = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % quotes.length);
      }, 25000);

      setIntervalId(newInterval);
    }
  };

  useEffect(() => {
    startInterval();
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [quotes.length, carousel]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % quotes.length);
    startInterval(); // Reset interval after manual navigation
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + quotes.length) % quotes.length);
    startInterval(); // Reset interval after manual navigation
  };

  const isFirstSlide = currentSlide === 0;
  const isLastSlide = currentSlide === quotes.length - 1;

  return (
    <div className="relative w-full mx-auto overflow-hidden">
      {carousel && (
        <div className="absolute w-full h-full grid grid-cols-12 content-end pb-8">
          <div className="col-span-12 bottom-4 px-8 flex justify-end space-x-2">
            <div className="ps-8 flex gap-4 z-10">
              <button
                onClick={prevSlide}
                className="cursor-pointer"
                disabled={isFirstSlide}
              >
                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 32 32"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M31.5 4C31.5 2.067 29.933 0.5 28 0.5H4C2.067 0.5 0.5 2.067 0.5 4V28C0.5 29.933 2.067 31.5 4 31.5H28C29.933 31.5 31.5 29.933 31.5 28V4Z"
                    fill="white"
                  />
                  <path
                    d="M31.5 4C31.5 2.067 29.933 0.5 28 0.5H4C2.067 0.5 0.5 2.067 0.5 4V28C0.5 29.933 2.067 31.5 4 31.5H28C29.933 31.5 31.5 29.933 31.5 28V4Z"
                    stroke="url(#paint0_linear_658_18026)"
                  />
                  <path
                    d="M19 22L13 16L19 10"
                    stroke={isFirstSlide ? '#c0c0c0' : '#0E0C0C'}
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <defs>
                    <linearGradient
                      id="paint0_linear_658_18026"
                      x1="72"
                      y1="-56.5"
                      x2="-7.5"
                      y2="45.5"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop />
                      <stop offset="1" stopColor="white" />
                    </linearGradient>
                  </defs>
                </svg>
              </button>
              <button
                onClick={nextSlide}
                className="cursor-pointer"
                disabled={isLastSlide}
              >
                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 32 32"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect
                    x="0.5"
                    y="0.5"
                    width="31"
                    height="31"
                    rx="3.5"
                    fill="white"
                  />
                  <rect
                    x="0.5"
                    y="0.5"
                    width="31"
                    height="31"
                    rx="3.5"
                    stroke="url(#paint0_linear_2413_18877)"
                  />
                  <path
                    d="M13 22L19 16L13 10"
                    stroke={isLastSlide ? '#c0c0c0' : '#0E0C0C'}
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <defs>
                    <linearGradient
                      id="paint0_linear_2413_18877"
                      x1="-40"
                      y1="-56.5"
                      x2="39.5"
                      y2="45.5"
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop />
                      <stop offset="1" stopColor="white" />
                    </linearGradient>
                  </defs>
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}
      <div
        className="flex transition-transform duration-500 ease-in-out"
        style={
          carousel ? { transform: `translateX(-${currentSlide * 100}%)` } : {}
        }
      >
        {quotes.map(({ id, image, quote, author, position, title, link }) => (
          <div key={id} className="w-full flex-shrink-0 grid grid-cols-12">
            {image?.src && (
              <div className="hidden lg:flex md:col-span-4 justify-center items-center p-8 rounded-lg relative">
                <img
                  src={image.src}
                  alt={image.alt}
                  className="h-full w-full object-contain rounded-lg"
                />
                <div className="absolute top-2 right-7">
                  <svg
                    width="6"
                    height="384"
                    viewBox="0 0 6 384"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M3 0L3.00002 384"
                      stroke="url(#paint0_linear_2505_78961)"
                    />
                    <path d="M3 19V25" stroke="#FF622B" />
                    <path d="M6 22L0 22" stroke="#FF622B" />
                    <path d="M3 362V368" stroke="#FF622B" />
                    <path d="M6 365L0 365" stroke="#FF622B" />
                    <defs>
                      <linearGradient
                        id="paint0_linear_2505_78961"
                        x1="2.5"
                        y1="2.18557e-08"
                        x2="2.50002"
                        y2="384"
                        gradientUnits="userSpaceOnUse"
                      >
                        <stop stopOpacity="0" />
                        <stop offset="0.575" stopColor="#3B3B3B" />
                        <stop offset="1" stopColor="#666666" stopOpacity="0" />
                      </linearGradient>
                    </defs>
                  </svg>
                </div>
              </div>
            )}
            <div className="col-span-12 lg:col-span-8 flex bg-gradient-to-b from-[#F7F7F7] to-white">
              <div className="flex flex-col justify-start relative px-2 md:px-8 pt-16">
                <div className="absolute top-2 left-0">
                  <svg
                    width="6"
                    height="384"
                    viewBox="0 0 6 384"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M3 0L3.00002 384"
                      stroke="url(#paint0_linear_2505_78961)"
                    />
                    <path d="M3 19V25" stroke="#FF622B" />
                    <path d="M6 22L0 22" stroke="#FF622B" />
                    <path d="M3 362V368" stroke="#FF622B" />
                    <path d="M6 365L0 365" stroke="#FF622B" />
                    <defs>
                      <linearGradient
                        id="paint0_linear_2505_78961"
                        x1="2.5"
                        y1="2.18557e-08"
                        x2="2.50002"
                        y2="384"
                        gradientUnits="userSpaceOnUse"
                      >
                        <stop stopOpacity="0" />
                        <stop offset="0.575" stopColor="#3B3B3B" />
                        <stop offset="1" stopColor="#666666" stopOpacity="0" />
                      </linearGradient>
                    </defs>
                  </svg>
                </div>
                <p className="text-sm text-orange mb-4">{title}</p>
                <blockquote className="text-lg">"{quote}"</blockquote>
                {link && (
                  <LinkArrow
                    to={link}
                    color="orange"
                    size="sm"
                    className="mt-4"
                  >
                    Read the full story
                  </LinkArrow>
                )}
                <p className="mt-4">
                  <span className="font-semibold">{author}</span>,{' '}
                  <span className="font-light text-gray-600">{position}</span>
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const TestimonialsSection = ({ title, items }) => {
  const formattedQuotes = items.map((item, index) => ({
    id: index,
    image: { src: item.logo, alt: item.company },
    quote: item.quote,
    author: item.author,
    position: item.authorTitle,
    title: item.title,
    link: item.link,
  }));

  return (
    <section className="py-14 lg:py-20 overflow-hidden relative">
      <h2 className="text-2xl lg:text-5xl font-medium mb-10 px-2 md:px-8">{title}</h2>
      <svg
        width="1260"
        height="2"
        viewBox="0 0 1260 2"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path d="M0 1H1260" stroke="#DBDBDB" />
      </svg>
      {formattedQuotes && (
        <Carousel quotes={formattedQuotes} carousel={items.length > 1} />
      )}
      <svg
        width="1260"
        height="2"
        viewBox="0 0 1260 2"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path d="M0 1H1260" stroke="#DBDBDB" />
      </svg>
    </section>
  );
};
